import PageHero from "../PageHero.jsx";
import ActivityChart from "../ActivityChart.jsx";
import ScrollArea from "../ScrollArea.jsx";
import TraceLogPanel from "../TraceLogPanel.jsx";
import CommandLogPanel from "../CommandLogPanel.jsx";
import RedactedByPolicy from "../RedactedByPolicy.jsx";
import { NOTE_CLASS } from "../../lib/ui.js";
import { useMemo } from "react";
import { useDebouncedValue } from "../../lib/useDebouncedValue.js";
import { compilePipeline } from "../../lib/queryPipeline.js";

// Scope-overview lens body — the universal fallback lens (see lensRegistry.js).
// Event-centric: hero + activity surface + trace/execution log for whatever
// scope is committed. Extracted verbatim from ScopedView's former inline
// leftContent so the reseat is behavior-preserving.
//
// Data-ownership note: this body reads its data from `ctx`, which the host
// (ScopedView) pre-fetches and assembles. `ctx` is the GRANDFATHERED overview's
// bag, NOT a shared services bus — new lenses must own their data via their own
// hooks (see the signals console) and must not add keys here. When the overview
// should own its fetching, move those queries into this body and drop the ctx
// keys: a per-lens migration, never a big-bang. LensHost keys every body by
// lens.id, so a body that owns hooks stays isolated across lens switches.
export default function ScopeOverviewBody({ slot, ctx }) {
  const { expanded, onToggleExpand, onExpandDetail } = slot;
  const {
    hero,
    plotConfig,
    activityLoading,
    baselines,
    range,
    activityLens,
    onRangeChange,
    onActivityLensChange,
    onHoverCluster,
    onHoverCell,
    onClickCell,
    hoveredCell,
    traces,
    clusters,
    palette,
    selectedCluster,
    selectedTrace,
    surfaceTitle,
    surfaceScope,
    surfaceScopeOverride,
    traceLog,
    traceStats,
    // The trace log's session grain: one row per conversation (getSessionLog),
    // with its own loading / denial / sort / paging. Search and the query bar
    // are shared with the request grain through traceLog.
    sessionLog = {},
    sessions = [],
    logTab,
    setLogTab,
    commandLog,
    commands,
    // RBAC sensitive-content gates. Each log surfaces data the role may not be
    // allowed to read: the Trace log selects enriched prompt/response text
    // (ai_sessions sensitive) and the Execution log selects raw event payloads
    // (events sensitive). The host (ScopedPage) keys these on the matching
    // `read.sensitive`, so a role without it gets the panel HIDDEN rather than
    // an ambiguous empty "no data" state. Both default open for non-RBAC embeds.
    canTracePrompts = true,
    promptsWithheldHere = false,
    canRawCommands = true,
  } = ctx;

  // A role that can read neither log (e.g. Viewer, no ai_sessions/events
  // read.sensitive) drops the whole trace/execution section below. Rather
  // than leave the activity surface floating at its content height over an
  // empty scroll area, let it fill the panel: switch the grid to a
  // hero-then-fill row template and hand the chart a definite 1fr track.
  const logHidden = !canTracePrompts && !canRawCommands;
  // The trace log owns two of the three log values; anything that is not the
  // Execution log is the trace log, at the session grain unless Requests was
  // chosen.
  const traceLogActive = logTab !== "commands";
  const sessionsGrain = traceLogActive && logTab !== "requests";

  // A `|>` pipeline in the query bar renders its chart in the activity surface
  // (the "kernel spot"), narrowed to the active scope — the trace log below stays
  // a trace log (filtered by the filter stage). Debounced so it doesn't recompile
  // / re-query on every keystroke; a non-pipeline query leaves the surface alone.
  const [pipelineQuery] = useDebouncedValue(traceLog?.query || "", 350);
  const pipeline = useMemo(() => {
    const r = compilePipeline(pipelineQuery, { scopeMode: "scoped" });
    return r.isPipeline ? r : null;
  }, [pipelineQuery]);

  return (
    <ScrollArea
      data-content-scroll
      className="min-h-0 h-full"
      contentClassName={
        logHidden
          ? "grid gap-[12px] pb-[12px] h-full grid-rows-[auto_minmax(0,1fr)]"
          : "grid gap-[12px] pb-[18px]"
      }
    >
      <div data-expand-collapse>
        <PageHero
          eyebrow={hero.eyebrow}
          title={hero.title}
          detail={hero.detail}
          metrics={hero.metrics}
          controls={hero.controls}
        />
      </div>

      <ActivityChart
        // Inline, the surface is a row in this auto-sized CSS grid and must
        // size to its content. ActivityChart's Sheet defaults to `h-full`
        // (height:100%) — fine when the parent row is definite (expanded →
        // ContentShell's fullscreen target; logHidden → the 1fr grid track
        // above), but against an auto grid row that percentage is indefinite:
        // Chrome resolves it to content, WebKit stretches it to the scroll
        // container, ballooning the panel with a dead gap below the chart.
        // Only fill when the row is definite.
        className={expanded || logHidden ? "h-full" : ""}
        fill={logHidden}
        plotConfig={plotConfig}
        loading={activityLoading}
        baselines={baselines}
        range={range}
        activityLens={activityLens}
        onRangeChange={onRangeChange}
        onLensChange={onActivityLensChange}
        onHoverCluster={onHoverCluster}
        onHoverCell={onHoverCell}
        onClickCell={onClickCell ? (cell) => { onClickCell(cell); onExpandDetail?.(); } : undefined}
        hoveredCell={hoveredCell}
        onToggleExpand={onToggleExpand}
        expanded={expanded}
        traces={traces}
        clusters={clusters}
        palette={palette}
        selectedCluster={selectedCluster}
        selectedTrace={selectedTrace}
        eyebrow={hero.chartEyebrow || "Activity surface"}
        title={surfaceTitle || hero.chartTitle || `${hero.title} activity`}
        chartMinHeight={traceLog.showEndpoint ? "260px" : "198px"}
        scopePath={surfaceScope}
        autoResetLens={!surfaceScopeOverride}
        inPinMode={surfaceScopeOverride != null}
        pipeline={pipeline}
      />

      {(canTracePrompts || canRawCommands) && (
      <div data-lock-width data-expand-collapse>
        {/* Tabs/panels are gated per sensitive read (see ctx destructure): the
            Trace log requires ai_sessions read.sensitive, the Execution log
            requires events read.sensitive. A log the role can't read keeps its
            tab but renders a "hidden by policy" marker in place of contents
            (legible parity with the raw-payload RedactedByPolicy marker),
            rather than silently vanishing; the whole section is dropped only
            when neither read is permitted. */}
        <div
          className="relative grid items-stretch gap-[2px]"
          style={{ gridTemplateColumns: canTracePrompts && canRawCommands ? "1fr 1fr" : "1fr" }}
        >
          <span className="pointer-events-none absolute inset-x-0 bottom-0 h-[1px] bg-[color:var(--line)]" aria-hidden="true" />
          {canTracePrompts ? (
          // The trace log reads at two grains, both under one tab: Sessions
          // (the default) is one row per conversation ordered by its newest
          // request; Requests is the request-grain list. The tab's title names
          // the grain showing; the switch between them is a chip pair on the
          // panel's summary line (TraceLogPanel, onModeChange). The tab itself,
          // when the Execution log is showing, brings the trace log back at
          // the session grain.
          <button
            type="button"
            data-log-tab="trace"
            onClick={traceLogActive ? undefined : () => setLogTab("sessions")}
            className={[
              "relative grid gap-[2px] border border-b-0 px-[11px] py-[8px] text-left",
              traceLogActive
                ? "border-[color:var(--line-strong)] bg-[color:var(--bg)] cursor-default"
                : "border-[color:var(--line)] bg-[color:var(--control-bg)] cursor-pointer",
            ].join(" ")}
          >
            {traceLogActive && <span className="pointer-events-none absolute inset-x-0 bottom-0 h-[1px] bg-[color:var(--bg)]" aria-hidden="true" />}
            <span className={["text-[11px] leading-[1.1] tracking-[0.06em] uppercase", traceLogActive ? "text-[color:var(--eyebrow)]" : "text-[color:var(--muted)]"].join(" ")}>Trace log</span>
            <span data-log-grain-title className={["text-[14px] leading-[1.06] font-medium tracking-[-0.04em]", traceLogActive ? "text-[color:var(--ink)]" : "text-[color:var(--muted)]"].join(" ")}>
              {sessionsGrain || !traceLogActive ? "Sessions" : (traceLog.title || "Requests")}
            </span>
          </button>
          ) : null}
          {canRawCommands ? (
          <button
            type="button"
            onClick={() => setLogTab("commands")}
            className={[
              "relative grid gap-[2px] border border-b-0 px-[11px] py-[8px] text-right cursor-pointer",
              logTab === "commands"
                ? "border-[color:var(--line-strong)] bg-[color:var(--bg)]"
                : "border-[color:var(--line)] bg-[color:var(--control-bg)]",
            ].join(" ")}
          >
            {logTab === "commands" && <span className="pointer-events-none absolute inset-x-0 bottom-0 h-[1px] bg-[color:var(--bg)]" aria-hidden="true" />}
            <span className={["text-[11px] leading-[1.1] tracking-[0.06em] uppercase", logTab === "commands" ? "text-[color:var(--eyebrow)]" : "text-[color:var(--muted)]"].join(" ")}>Execution log</span>
            <span className={["text-[14px] leading-[1.06] font-medium tracking-[-0.04em]", logTab === "commands" ? "text-[color:var(--ink)]" : "text-[color:var(--muted)]"].join(" ")}>{commandLog.title || "Commands"}</span>
          </button>
          ) : null}
        </div>
        {(!canTracePrompts || promptsWithheldHere) && (traceLogActive || !canRawCommands) ? (
          // The caller reads no prompt/response text in this scope: either the
          // role lacks ai_sessions sensitive, or it holds it through an
          // escalation that does not cover here.
          // Keep the surface legible — show a "hidden by policy" marker in the
          // log's place, mirroring the raw-payload RedactedByPolicy affordance,
          // rather than rendering nothing. Doesn't change what data is shown.
          <div className="grid place-items-start gap-[6px] border border-t-0 border-[color:var(--line)] px-[11px] py-[14px]">
            <RedactedByPolicy what="Request prompt and response contents" />
            <span className={NOTE_CLASS}>
              Message contents are hidden by your organization&apos;s data-access policy.
            </span>
          </div>
        ) : canTracePrompts && !promptsWithheldHere && (traceLogActive || !canRawCommands) ? (
          <TraceLogPanel
            mode={sessionsGrain ? "sessions" : "requests"}
            onModeChange={setLogTab}
            traces={sessionsGrain ? sessions : traces}
            totalTokens={traceStats.totalTokens}
            tokenBreakdown={traceStats.tokenBreakdown}
            tokenCost={traceStats.tokenCost}
            selectedTraceId={traceLog.selectedTraceId}
            onSelectTrace={(id, row) => { traceLog.onSelectTrace(id, row); onExpandDetail?.(); }}
            showEndpoint={traceLog.showEndpoint}
            title=""
            description={traceLog.description}
            searchPlaceholder={traceLog.searchPlaceholder}
            loading={sessionsGrain ? sessionLog.loading : traceLog.loading}
            denial={sessionsGrain ? sessionLog.denial : traceLog.denial}
            sort={sessionsGrain ? sessionLog.sort : traceLog.sort}
            onSortChange={sessionsGrain ? sessionLog.onSortChange : traceLog.onSortChange}
            searchQuery={traceLog.searchQuery}
            onSearchChange={traceLog.onSearchChange}
            query={traceLog.query}
            onQueryChange={traceLog.onQueryChange}
            suggestValues={traceLog.suggestValues}
            onLoadMore={sessionsGrain ? sessionLog.onLoadMore : traceLog.onLoadMore}
          />
        ) : (
          <CommandLogPanel
            commands={commands}
            selectedId={commandLog.selectedId}
            onSelect={(id) => { commandLog.onSelect(id); onExpandDetail?.(); }}
            showEndpoint={commandLog.showEndpoint}
            title=""
            searchPlaceholder={commandLog.searchPlaceholder || "Command, tool, session, endpoint"}
            sort={commandLog.sort}
            onSortChange={commandLog.onSortChange}
            searchQuery={commandLog.searchQuery}
            onSearchChange={commandLog.onSearchChange}
            eventTypes={commandLog.eventTypes}
            onEventTypesChange={commandLog.onEventTypesChange}
            onLoadMore={commandLog.onLoadMore}
            maxRows={commandLog.maxRows}
            loading={commandLog.loading}
            contextOn={commandLog.contextOn}
            onContextToggle={commandLog.onContextToggle}
            contextWindowMs={commandLog.contextWindowMs}
            onContextWindowChange={commandLog.onContextWindowChange}
            contextDirection={commandLog.contextDirection}
            onContextDirectionChange={commandLog.onContextDirectionChange}
            contextFocalOnly={commandLog.contextFocalOnly}
            onContextFocalOnlyChange={commandLog.onContextFocalOnlyChange}
            focalTraceId={commandLog.focalTraceId}
            focalTraceTimestamp={commandLog.focalTraceTimestamp}
            attributionByCommand={commandLog.attributionByCommand}
            traceLookup={commandLog.traceLookup}
            onOpenTrace={commandLog.onOpenTrace}
            denial={commandLog.denial}
          />
        )}
      </div>
      )}
    </ScrollArea>
  );
}
